import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts';

/**
 * Utility function to parse UTC timestamp consistently
 */
const parseUTCTimestamp = (timestamp) => {
  const timestampStr = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  return new Date(timestampStr);
};

/**
 * TradingChart - Visual representation of trading matches
 * Supports Line, Bar, Area, and Candlestick chart types
 * Client-side timeframe filtering for instant response
 */
const TradingChart = ({ data = [], availableInstruments = [], selectedInstrument, onInstrumentChange, timeframe, onTimeframeChange }) => {
  const [chartType, setChartType] = useState('line');
  const [renderKey, setRenderKey] = useState(0);
  
  // Force re-render when data changes
  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [data]);

  // Force refresh every 30 seconds to ensure chart updates
  useEffect(() => {
    const interval = setInterval(() => {
      setRenderKey(prev => prev + 1);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Transform and filter data by selected instrument and timeframe (CLIENT-SIDE)
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Get current time in local timezone
    const now = new Date(); // Current time in local timezone
    const nowMs = now.getTime(); // Local time in milliseconds
    
    // Filter by instrument and timeframe first, then transform
    const filtered = data.filter(match => {
      // Filter by instrument
      if (match.instrument !== selectedInstrument) return false;
      
      // Filter by timeframe
      if (!match.timestamp) return true; // Keep if no timestamp
      
      const tradeTime = parseUTCTimestamp(match.timestamp);
      
      // Skip invalid timestamps
      if (isNaN(tradeTime.getTime())) return false;
      
      // Convert server UTC timestamp to local time for comparison
      const tradeTimeMs = tradeTime.getTime(); // This converts UTC to local time automatically
      const diffMs = Math.abs(nowMs - tradeTimeMs);
      
      switch (timeframe) {
        case '1m':
          return diffMs <= 60 * 1000; // 1 minute
        case '1h':
          return diffMs <= 60 * 60 * 1000; // 1 hour (only last hour)
        case '1d':
          return diffMs <= 24 * 60 * 60 * 1000; // 1 day (only last 24 hours)
        case '1mo':
          return diffMs <= 30 * 24 * 60 * 60 * 1000; // 30 days
        default:
          return true; // Show all
      }
    });

    // Transform filtered data to chart format and reverse order so newest is on the right
    return filtered.map(match => {
      // Use existing time if available, otherwise parse timestamp
      let timeString = match.time;
      if (!timeString || timeString === 'Invalid Time') {
        try {
          const date = parseUTCTimestamp(match.timestamp);
          if (!isNaN(date.getTime())) {
            // Convert to local time for display (but keep UTC for calculations)
            timeString = date.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            });
          } else {
            timeString = 'Invalid Time';
          }
        } catch (error) {
          console.warn('Failed to parse timestamp:', match.timestamp, error);
          timeString = 'Invalid Time';
        }
      }
      
      return {
        time: timeString,
        price: match.price,
        volume: match.volume || match.quantity,
        timestamp: match.timestamp,
        timestampUTC: new Date(match.timestamp).getTime(), // Store UTC milliseconds for reference
        instrument: match.instrument || 'AMBER'
      };
    }).reverse(); // Reverse so newest data appears on the right
  }, [data, selectedInstrument, timeframe]);

  // Debug logging for development (can be removed in production)
  if (process.env.NODE_ENV === 'development') {
    console.log(`Chart Debug - Timeframe: ${timeframe}, Total data: ${data.length}, Filtered: ${filteredData.length}`);
  }

  const renderChart = (chartData) => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 5, left: 5, bottom: 5 },
      // Force re-render when data changes
      key: `chart-${chartData.length}-${chartData.length > 0 ? chartData[chartData.length - 1].timestamp : 'empty'}`
    };

    const commonAxisProps = {
      hide: true
    };

    const commonTooltipProps = {
      contentStyle: {
        background: "rgba(20,20,30,0.95)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 8,
        color: 'var(--text)'
      },
      labelStyle: { color: 'var(--text)' },
      formatter: (value, name) => [
        name === 'price' ? `$${value.toFixed(2)}` : `${value} shares`,
        name === 'price' ? 'Price' : 'Volume'
      ],
      labelFormatter: (label, payload) => {
        if (payload && payload[0]) {
          return `${payload[0].payload.instrument} - ${label}`;
        }
        return label;
      }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <XAxis dataKey="time" {...commonAxisProps} />
            <YAxis {...commonAxisProps} domain={['dataMin - 10', 'dataMax + 10']} />
            <Tooltip {...commonTooltipProps} />
            <Bar dataKey="price" fill="var(--accent)" />
          </BarChart>
        );
      
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <XAxis dataKey="time" {...commonAxisProps} />
            <YAxis {...commonAxisProps} domain={['dataMin - 10', 'dataMax + 10']} />
            <Tooltip {...commonTooltipProps} />
            <Area 
              type="monotone" 
              dataKey="price" 
              stroke="var(--accent)" 
              fill="var(--accent)" 
              fillOpacity={0.3}
            />
          </AreaChart>
        );
      
      case 'candlestick':
        // Generate realistic OHLC data from price sequence without self-referencing during construction
        const candlestickData = [];
        for (let i = 0; i < chartData.length; i++) {
          const item = chartData[i];
          const basePrice = item.price;
          
          if (i === 0) {
            // First candle: use simulated open near close
            const open = basePrice * (0.98 + Math.random() * 0.04); // ±2%
            candlestickData.push({
              ...item,
              open,
              high: Math.max(open, basePrice) * (1 + Math.random() * 0.01),
              low: Math.min(open, basePrice) * (1 - Math.random() * 0.01),
              close: basePrice
            });
          } else {
            // Subsequent candles: previous close becomes open
            const prevClose = candlestickData[i - 1].close;
            const priceChange = prevClose ? (basePrice - prevClose) / prevClose : 0;
            const volatility = Math.min(Math.abs(priceChange) * 2, 0.05); // cap at 5%
            const open = prevClose || basePrice;
            const close = basePrice;
            const high = Math.max(open, close) * (1 + Math.random() * volatility);
            const low = Math.min(open, close) * (1 - Math.random() * volatility);
            candlestickData.push({
              ...item,
              open,
              high,
              low,
              close
            });
          }
        }
        
        // Calculate min/max for scaling
        const minPrice = Math.min(...candlestickData.map(d => d.low));
        const maxPrice = Math.max(...candlestickData.map(d => d.high));
        const priceRange = maxPrice - minPrice || 1; // Avoid division by zero
        
        // Use viewBox for proper scaling
        const viewBoxWidth = 1000;
        const viewBoxHeight = 250;
        const padding = 20;
        const chartHeight = viewBoxHeight - (padding * 2);
        
        // Calculate dynamic bar width based on data count
        const barSpacing = Math.max(2, (viewBoxWidth - padding * 2) / candlestickData.length);
        const barWidth = Math.min(barSpacing * 0.7, 20); // Max 20px width
        
        return (
          <div style={{position: 'relative', width: '100%', height: '100%'}}>
            <svg 
              width="100%" 
              height="100%" 
              viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
              preserveAspectRatio="none"
              style={{position: 'absolute', inset: 0}}
            >
              {candlestickData.map((item, index) => {
                const isGreen = item.close >= item.open;
                const color = isGreen ? '#10b981' : '#ef4444';
                
                // Calculate x position with proper spacing
                const xPos = padding + (index * barSpacing) + (barSpacing / 2);
                
                // Scale prices to chart height
                const highY = padding + ((maxPrice - item.high) / priceRange) * chartHeight;
                const lowY = padding + ((maxPrice - item.low) / priceRange) * chartHeight;
                const openY = padding + ((maxPrice - item.open) / priceRange) * chartHeight;
                const closeY = padding + ((maxPrice - item.close) / priceRange) * chartHeight;
                
                return (
                  <g key={index}>
                    {/* High-Low line (wick) */}
                    <line
                      x1={xPos}
                      x2={xPos}
                      y1={highY}
                      y2={lowY}
                      stroke={color}
                      strokeWidth={1}
                    />
                    {/* Open-Close rectangle (body) */}
                    <rect
                      x={xPos - (barWidth / 2)}
                      y={Math.min(openY, closeY)}
                      width={barWidth}
                      height={Math.abs(closeY - openY) || 1}
                      fill={isGreen ? color : 'transparent'}
                      stroke={color}
                      strokeWidth={1}
                    />
                  </g>
                );
              })}
            </svg>
          </div>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <XAxis dataKey="time" {...commonAxisProps} />
            <YAxis {...commonAxisProps} domain={['dataMin - 10', 'dataMax + 10']} />
            <Tooltip {...commonTooltipProps} />
            <Line 
              type="monotone" 
              dataKey="price" 
              stroke="var(--accent)" 
              strokeWidth={3} 
              dot={false}
              activeDot={{ r: 4, fill: "var(--accent)" }}
              key={`line-${filteredData.length}-${renderKey}`}
            />
          </LineChart>
        );
    }
  };

  const chartTypeButtons = [
    { id: 'line', label: 'Line' },
    { id: 'area', label: 'Area' },
    { id: 'bar', label: 'Bar' },
    { id: 'candlestick', label: 'Candle' },
  ];

  const timeframeButtons = [
    { id: '1m', label: '1m' },
    { id: '1h', label: '1h' },
    { id: '1d', label: '1d' },
    { id: '1mo', label: '1mo' },
  ];

  return (
    <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid var(--border)'}}>
        <h3 style={{margin: 0, fontSize: '14px', color: 'var(--muted)'}}>Trading Chart</h3>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          {/* Instrument Selector */}
          {availableInstruments.length > 0 && (
            <select 
              value={selectedInstrument}
              onChange={(e) => onInstrumentChange(e.target.value)}
              style={{
                background: 'var(--panel)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                padding: '6px 10px',
                borderRadius: '8px',
                fontSize: '12px'
              }}
            >
              {availableInstruments.map(inst => (
                <option key={inst} value={inst}>{inst}</option>
              ))}
            </select>
          )}
          
          {/* Timeframe Buttons */}
          <div style={{display: 'flex', gap: '4px', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '8px', padding: '2px'}}>
            {timeframeButtons.map(btn => (
              <button
                key={btn.id}
                onClick={() => onTimeframeChange(btn.id)}
                style={{
                  background: timeframe === btn.id ? 'var(--accent)' : 'transparent',
                  color: timeframe === btn.id ? 'white' : 'var(--muted)',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
          
          {/* Chart Type Buttons */}
          <div style={{display: 'flex', gap: '4px', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '8px', padding: '2px'}}>
            {chartTypeButtons.map(btn => (
              <button
                key={btn.id}
                onClick={() => setChartType(btn.id)}
                style={{
                  background: chartType === btn.id ? 'var(--accent)' : 'transparent',
                  color: chartType === btn.id ? 'white' : 'var(--muted)',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div style={{padding: '12px', height: '300px'}}>
        {filteredData.length > 0 ? (
          chartType === 'candlestick' ? (
            // Candlestick chart doesn't use ResponsiveContainer
            renderChart(filteredData)
          ) : (
            // Other charts use ResponsiveContainer
            <ResponsiveContainer 
              width="100%" 
              height="100%" 
              key={`chart-${renderKey}`}
            >
              {renderChart(filteredData)}
            </ResponsiveContainer>
          )
        ) : (
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--muted)', fontSize: '13px'}}>
            No trading data available for {selectedInstrument} in the selected timeframe
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingChart;

